import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { useCart } from '../context/CartContext';
import { useMenu } from '../context/MenuContext';
import { submitOrder, fetchStripeConfig, createPaymentIntent, fetchOnlineStatus, checkAddress } from '../api';
import { PaymentMethodPicker } from './PaymentMethodPicker';
import { PaymentForm } from './PaymentForm';
import { useI18n } from '../i18n';
import type { OrderPayload, DeliveryZone, DeliveryZoneFeeItem } from '../types';

interface MatchedZoneSummary {
  id: number;
  name: string;
  minimum_order: number | null;
  delivery_fee_item_id: number | null;
  delivery_fee_item: DeliveryZoneFeeItem | null;
}

/** Read-only zone polygon rendered on the map */
function ZonePolygonOverlay({ zone }: { zone: DeliveryZone }) {
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map || !zone.shape_json || zone.shape_json.length < 3) return;

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
    }

    const poly = new google.maps.Polygon({
      paths: zone.shape_json,
      strokeColor: zone.color || '#FF0000',
      strokeWeight: 2,
      fillColor: zone.color || '#FF0000',
      fillOpacity: 0.15,
      clickable: false,
      map,
    });
    polygonRef.current = poly;

    // Fit bounds to polygon
    const bounds = new google.maps.LatLngBounds();
    zone.shape_json.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 30);

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [map, zone]);

  return null;
}

export function CheckoutForm() {
  const { items, totalPrice, setView, clearCart, setOrderUuid } = useCart();
  const { defaultLocationId, deliveryZones, googleApiKey } = useMenu();
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [floor, setFloor] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card'>('cash');
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showStripeForm, setShowStripeForm] = useState(false);

  // Zone validation state
  const [addressCoords, setAddressCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [addressOutOfZone, setAddressOutOfZone] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [matchedZone, setMatchedZone] = useState<MatchedZoneSummary | null>(null);

  // Zones with polygon data
  const zonesWithPolygon = deliveryZones.filter(
    (z) => z.shape_json && Array.isArray(z.shape_json) && z.shape_json.length >= 3
  );
  const hasZoneValidation = zonesWithPolygon.length > 0 && !!googleApiKey;

  // Load Stripe publishable key on mount
  useEffect(() => {
    fetchStripeConfig()
      .then((config) => {
        if (config.publishable_key) {
          setStripePromise(loadStripe(config.publishable_key));
        }
      })
      .catch(() => {
        // Stripe not configured — card payments will not be available
      });
  }, []);

  const geocodeAddress = useCallback(async (addr: string) => {
    if (!addr.trim() || !hasZoneValidation) return;

    setGeocoding(true);
    setAddressOutOfZone(false);
    setAddressCoords(null);
    setMatchedZone(null);

    try {
      const result = await checkAddress(addr.trim());
      if (result.lat != null && result.lng != null) {
        setAddressCoords({ lat: result.lat, lng: result.lng });
        setAddressOutOfZone(!result.in_zone);
        setMatchedZone(result.zone ?? null);
      }
    } catch {
      // Geocoding failed — don't block, just skip validation
    } finally {
      setGeocoding(false);
    }
  }, [hasZoneValidation]);

  function handleAddressChange(value: string) {
    setAddress(value);
    setAddressCoords(null);
    setAddressOutOfZone(false);
    setMatchedZone(null);
  }

  function handleAddressBlur() {
    if (address.trim()) {
      geocodeAddress(address);
    }
  }

  const feeItem = matchedZone?.delivery_fee_item ?? null;
  const deliveryFee = feeItem ? Number(feeItem.price) : 0;
  const minimumOrder = matchedZone?.minimum_order ?? null;
  const grandTotal = totalPrice + deliveryFee;

  function buildPayload(ccPayment: boolean): OrderPayload {
    const orders: OrderPayload['orders'] = [];
    for (const ci of items) {
      for (let i = 0; i < ci.quantity; i++) {
        orders.push({
          id: ci.item.id,
          count: 1,
          addons: ci.addons.map((a) => ({ id: a.id })),
          note: ci.note || undefined,
        });
      }
    }
    // The delivery fee is a real MenuItem configured on the matched zone.
    // Append it as its own order line so it persists on the delivery record
    // and renders on the ticket like any other item. Backend validates that
    // exactly one fee line is present when the zone defines one.
    if (feeItem) {
      orders.push({ id: feeItem.id, count: 1, addons: [] });
    }

    return {
      orders,
      customer_address: {
        name: name.trim(),
        address: address.trim(),
        contact_phone: phone.trim(),
        floor: floor.trim() || undefined,
        extra: note.trim() || undefined,
      },
      email: email.trim(),
      location: { id: defaultLocationId ?? 0 },
      cc_payment: ccPayment,
      online_order: true,
    };
  }

  function validateForm(): boolean {
    if (!name.trim() || !address.trim() || !phone.trim() || !email.trim()) {
      setError(t('checkout.fillRequired'));
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      setError(t('checkout.emailInvalid'));
      return false;
    }
    if (!defaultLocationId) {
      setError(t('checkout.noLocation'));
      return false;
    }
    if (addressOutOfZone) {
      setError('Dostava nije dostupna na vašu adresu.');
      return false;
    }
    if (minimumOrder != null && totalPrice < minimumOrder) {
      setError(`Minimalni iznos narudžbe je ${minimumOrder.toFixed(2)} €. Trenutni iznos: ${totalPrice.toFixed(2)} €.`);
      return false;
    }
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Ensure address has been geocoded before submitting
    if (hasZoneValidation && address.trim() && !addressCoords && !geocoding) {
      setGeocoding(true);
      setAddressOutOfZone(false);
      setMatchedZone(null);
      try {
        const result = await checkAddress(address.trim());
        if (result.lat != null && result.lng != null) {
          setAddressCoords({ lat: result.lat, lng: result.lng });
          setAddressOutOfZone(!result.in_zone);
          setMatchedZone(result.zone ?? null);
          if (!result.in_zone) {
            setError('Dostava nije dostupna na vašu adresu.');
            setGeocoding(false);
            return;
          }
          if (result.zone?.minimum_order != null && totalPrice < result.zone.minimum_order) {
            setError(`Minimalni iznos narudžbe je ${result.zone.minimum_order.toFixed(2)} €. Trenutni iznos: ${totalPrice.toFixed(2)} €.`);
            setGeocoding(false);
            return;
          }
        }
      } catch {
        // Geocoding failed — proceed, backend validates too
      } finally {
        setGeocoding(false);
      }
    }

    // Re-check availability before submitting
    try {
      const status = await fetchOnlineStatus();
      if (status.is_paused) {
        setError('Online narudžbe su trenutno pauzirane. Pokušajte ponovo za nekoliko minuta.');
        return;
      }
      if (!status.is_open) {
        setError('Online narudžbe trenutno nije dostupne. Provjerite radno vrijeme.');
        return;
      }
    } catch {
      // If the check fails, proceed anyway — backend validates too
    }

    if (!validateForm()) return;

    if (paymentMethod === 'card' && stripePromise) {
      // Create PaymentIntent and show Stripe form
      setSubmitting(true);
      try {
        const amountCents = Math.round(grandTotal * 100);
        const { client_secret } = await createPaymentIntent(amountCents);
        setClientSecret(client_secret);
        setShowStripeForm(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize payment.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Cash payment — submit directly
    await submitOrderDirect(false);
  }

  async function submitOrderDirect(ccPayment: boolean, onlinePaid = false) {
    setSubmitting(true);
    try {
      const payload = buildPayload(ccPayment);
      if (onlinePaid) payload.online_paid = true;
      const result = await submitOrder(payload);
      setOrderUuid(result.uuid);
      clearCart();
      setView('waiting');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Order submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStripeSuccess(_paymentIntentId: string) {
    // Payment confirmed via Stripe — online_paid only, not cc_payment
    await submitOrderDirect(false, true);
  }

  // Show Stripe payment form
  if (showStripeForm && clientSecret && stripePromise) {
    return (
      <div className="hc-checkout">
        <div className="hc-checkout-header">
          <button className="hc-back-btn" onClick={() => setShowStripeForm(false)}>
            &larr; {t('checkout.back')}
          </button>
          <h2>{t('checkout.paymentCard')}</h2>
        </div>
        <div className="hc-checkout-form">
          <div className="hc-checkout-summary">
            <div className="hc-summary-line">
              <span>{t('checkout.items')}</span>
              <span>{totalPrice.toFixed(2)} &euro;</span>
            </div>
            {deliveryFee > 0 && (
              <div className="hc-summary-line">
                <span>{t('checkout.deliveryFee')}</span>
                <span>{deliveryFee.toFixed(2)} &euro;</span>
              </div>
            )}
            {matchedZone && deliveryFee === 0 && (
              <div className="hc-summary-line">
                <span>{t('checkout.deliveryFee')}</span>
                <span className="hc-summary-free">{t('checkout.free')}</span>
              </div>
            )}
            <div className="hc-summary-line hc-summary-total">
              <span>{t('checkout.total')}</span>
              <strong>{grandTotal.toFixed(2)} &euro;</strong>
            </div>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onSuccess={handleStripeSuccess}
              onCancel={() => setShowStripeForm(false)}
            />
          </Elements>
          {error && <div className="hc-form-error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="hc-checkout">
      <div className="hc-checkout-header">
        <button className="hc-back-btn" onClick={() => setView('cart')}>
          &larr; {t('checkout.back')}
        </button>
        <h2>{t('checkout.title')}</h2>
      </div>

      <form className="hc-checkout-form" onSubmit={handleSubmit}>
        <div className="hc-form-group">
          <label htmlFor="hc-name">{t('checkout.name')} *</label>
          <input
            id="hc-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('checkout.name')}
            maxLength={255}
            required
          />
        </div>

        <div className="hc-form-group">
          <label htmlFor="hc-address">{t('checkout.address')} *</label>
          <input
            id="hc-address"
            type="text"
            value={address}
            onChange={(e) => handleAddressChange(e.target.value)}
            onBlur={handleAddressBlur}
            placeholder={t('checkout.address')}
            maxLength={255}
            required
          />
          {geocoding && (
            <div className="hc-zone-checking">Provjeravamo adresu...</div>
          )}
          {addressOutOfZone && !geocoding && (
            <div className="hc-zone-warning">Dostava nije dostupna u vašem području</div>
          )}
        </div>

        <div className="hc-form-group">
          <label htmlFor="hc-floor">{t('checkout.floor')}</label>
          <input
            id="hc-floor"
            type="text"
            value={floor}
            onChange={(e) => setFloor(e.target.value)}
            placeholder={t('checkout.floor')}
            maxLength={255}
          />
        </div>

        <div className="hc-form-group">
          <label htmlFor="hc-phone">{t('checkout.phone')} *</label>
          <input
            id="hc-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+385..."
            maxLength={20}
            required
          />
        </div>

        <div className="hc-form-group">
          <label htmlFor="hc-email">{t('checkout.email')} *</label>
          <input
            id="hc-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vasa@mail.hr"
            maxLength={255}
            required
          />
        </div>

        <div className="hc-form-group">
          <label htmlFor="hc-note">{t('checkout.note')}</label>
          <textarea
            id="hc-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('checkout.note')}
            rows={3}
            maxLength={255}
          />
        </div>

        {hasZoneValidation && (
          <div className="hc-zone-map-container">
            <APIProvider apiKey={googleApiKey!}>
              <Map
                defaultCenter={
                  addressCoords ||
                  (zonesWithPolygon[0]?.shape_json?.[0]) ||
                  { lat: 45.8, lng: 15.97 }
                }
                defaultZoom={13}
                mapId="checkout-zone-map"
                style={{ width: '100%', height: '100%' }}
                gestureHandling="cooperative"
                disableDefaultUI
                zoomControl
              >
                {zonesWithPolygon.map((zone) => (
                  <ZonePolygonOverlay key={zone.id} zone={zone} />
                ))}
                {addressCoords && (
                  <AdvancedMarker position={addressCoords} />
                )}
              </Map>
            </APIProvider>
          </div>
        )}

        {stripePromise && (
          <PaymentMethodPicker selected={paymentMethod} onChange={setPaymentMethod} />
        )}

        <div className="hc-checkout-summary">
          <div className="hc-summary-line">
            <span>{t('checkout.items')}</span>
            <span>{totalPrice.toFixed(2)} &euro;</span>
          </div>
          {deliveryFee > 0 && (
            <div className="hc-summary-line">
              <span>{t('checkout.deliveryFee')}</span>
              <span>{deliveryFee.toFixed(2)} &euro;</span>
            </div>
          )}
          {matchedZone && deliveryFee === 0 && (
            <div className="hc-summary-line">
              <span>{t('checkout.deliveryFee')}</span>
              <span className="hc-summary-free">Besplatno</span>
            </div>
          )}
          <div className="hc-summary-line hc-summary-total">
            <span>Ukupno</span>
            <strong>{grandTotal.toFixed(2)} &euro;</strong>
          </div>
          {minimumOrder != null && totalPrice < minimumOrder && (
            <div className="hc-zone-warning" style={{ marginTop: 8 }}>
              Minimalni iznos narudžbe je {minimumOrder.toFixed(2)} &euro;
            </div>
          )}
        </div>

        {error && <div className="hc-form-error">{error}</div>}

        <button
          type="submit"
          className="hc-btn hc-btn-primary hc-btn-block"
          disabled={submitting}
        >
          {submitting
            ? t('checkout.submitting')
            : t('checkout.submit')}
        </button>
      </form>
    </div>
  );
}
